import { z } from 'zod'

import type { StatusTone } from '@ems/ui/patterns/status-badge'

import { PAYMENT_METHODS, PAYMENT_STATUSES } from '#admin/features/billing/payments-api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * Invoices, as the operator console sees them.
 *
 * Money arrives as a **number**, which is the stack contract (§11): exact everywhere behind
 * the API's boundary, converted once in its DTO. The console formats it and never does
 * arithmetic on it — any figure it needs is one the server already computed, because a total
 * recomputed in the browser is a second implementation of invariant 05.
 */
export const INVOICE_STATUSES = [
  'draft',
  'issued',
  'partially_paid',
  'paid',
  'overdue',
  'voided',
] as const

const lineSchema = z.object({
  id: z.string(),
  description: z.string(),
  quantity: z.string(),
  unitPrice: z.number(),
  discountAmount: z.number(),
  taxRate: z.string(),
  lineSubtotal: z.number(),
  lineTax: z.number(),
  lineTotal: z.number(),
})

/** The Indonesian VAT category of the payer. A WAPU company collects the VAT itself. */
export const COMPANY_CATEGORY_LABEL = {
  wapu: 'WAPU (wajib pungut)',
  non_wapu: 'Non-WAPU',
} as const
export type CompanyCategory = keyof typeof COMPANY_CATEGORY_LABEL
export const COMPANY_CATEGORIES = ['wapu', 'non_wapu'] as const satisfies readonly CompanyCategory[]

/** The transaction code of the tax invoice (kode transaksi faktur pajak). */
export const TAX_INVOICE_CODE_LABEL = {
  '030': '030 — sale to a VAT collector (WAPU)',
  '040': '040 — sale with a deemed base (nilai lain)',
} as const
export type TaxInvoiceCode = keyof typeof TAX_INVOICE_CODE_LABEL
export const TAX_INVOICE_CODES = ['030', '040'] as const satisfies readonly TaxInvoiceCode[]

export const invoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.enum(INVOICE_STATUSES),
  /** Finance's own paid/unpaid marker. Display only: nothing reads it to decide what is owed. */
  companyInvoiceStatus: z.enum(['unpaid', 'paid']),
  billToName: z.string(),
  billToCompanyId: z.string().nullable(),
  billToEmail: z.string().nullable(),
  billToAddress: z.string().nullable(),
  billToTaxId: z.string().nullable(),
  /** As on the NPWP, from a billing profile. For the tax invoice. */
  billToTaxName: z.string().nullable(),
  billToTaxAddress: z.string().nullable(),
  /** The billing profile the bill-to came from (the company invoice). */
  billToProfileId: z.string().nullable(),
  billToCompanyCategory: z.enum(COMPANY_CATEGORIES).nullable(),
  taxInvoiceCode: z.enum(TAX_INVOICE_CODES).nullable(),
  currency: z.string(),
  subtotal: z.number(),
  discountTotal: z.number(),
  taxTotal: z.number(),
  total: z.number(),
  amountPaid: z.number(),
  creditedTotal: z.number(),
  paymentCurrency: z.string().nullable(),
  paymentExchangeRate: z.string().nullable(),
  dueAt: z.string().nullable(),
  issuedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  notes: z.string().nullable(),
  /** For operators only. The customer never sees it. */
  internalNotes: z.string().nullable(),
  sourceModule: z.string().nullable(),
  sourceRef: z.string().nullable(),
  reissueOfInvoiceId: z.string().nullable(),
  /** The payment method that was selected at submit. It is null when nobody selected one. */
  paymentMethod: z
    .object({
      code: z.string(),
      name: z.string(),
      /** The company invoice: an operator checks the billing profile and accepts the invoice. */
      needsBillingProfile: z.boolean(),
    })
    .nullable(),
  createdAt: z.string(),
  lines: z.array(lineSchema).optional(),
})

const invoicePageSchema = z.object({
  items: z.array(invoiceSchema),
  nextCursor: z.string().nullable(),
  total: z.number(),
})

const summarySchema = z.object({
  counts: z.record(z.enum(INVOICE_STATUSES), z.number()),
  currencies: z.array(
    z.object({
      currency: z.string(),
      outstanding: z.number(),
      collected: z.number(),
      credited: z.number(),
      pastDueCount: z.number(),
      pastDue: z.number(),
    }),
  ),
})

const invoicePaymentsSchema = z.object({
  items: z.array(
    z.object({
      allocationId: z.string(),
      paymentId: z.string(),
      reference: z.string(),
      method: z.enum(PAYMENT_METHODS),
      status: z.enum(PAYMENT_STATUSES),
      payerName: z.string().nullable(),
      paidAt: z.string().nullable(),
      currency: z.string(),
      amount: z.number(),
      convertedAmount: z.number(),
      appliedExchangeRate: z.string().nullable(),
      allocatedAt: z.string(),
    }),
  ),
})

export type Invoice = z.infer<typeof invoiceSchema>
export type InvoiceLine = z.infer<typeof lineSchema>
export type InvoiceStatus = Invoice['status']
export type CompanyInvoiceStatus = Invoice['companyInvoiceStatus']
export type InvoiceSummary = z.infer<typeof summarySchema>
export type InvoicePayment = z.infer<typeof invoicePaymentsSchema>['items'][number]
export type InvoiceFilters = { q: string; status: InvoiceStatus | '' }

const base = '/v1/billing/admin'
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

export function fetchInvoices(
  filters: InvoiceFilters,
  cursor: string | undefined,
  { signal, limit = 50 }: { signal?: AbortSignal; limit?: number } = {},
) {
  const params = new URLSearchParams({ limit: String(limit) })
  for (const [key, value] of Object.entries(filters)) if (value !== '') params.set(key, value)
  if (cursor !== undefined) params.set('cursor', cursor)
  return api.get(`${base}/invoices?${params.toString()}`, {
    schema: invoicePageSchema,
    ...withSignal(signal),
  })
}

export function fetchInvoiceSummary(signal?: AbortSignal) {
  return api.get(`${base}/invoices/summary`, { schema: summarySchema, ...withSignal(signal) })
}

export function fetchInvoice(id: string, signal?: AbortSignal) {
  return api.get(`${base}/invoices/${id}`, { schema: invoiceSchema, ...withSignal(signal) })
}

/**
 * A payer's saved billing profile, for the company invoice check. The PIC is the contact for the
 * operator. It is not on the invoice.
 */
export const billingProfileSchema = z.object({
  id: z.string(),
  label: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string(),
  invoiceName: z.string(),
  invoiceAddress: z.string(),
  taxName: z.string(),
  taxAddress: z.string(),
  taxId: z.string(),
  taxInvoiceCode: z.enum(TAX_INVOICE_CODES),
  supportingDocuments: z.array(z.enum(['po', 'spk_loa', 'contract', 'gl', 'npwp'])),
})

export type BillingProfile = z.infer<typeof billingProfileSchema>

/** The documents the company gives the organizer, by their v1 names. */
export const SUPPORTING_DOCUMENT_LABELS: Record<
  BillingProfile['supportingDocuments'][number],
  string
> = {
  po: 'PO',
  spk_loa: 'SPK / LOA',
  contract: 'Contract',
  gl: 'GL',
  npwp: 'NPWP',
}

export function fetchBillingProfile(id: string, signal?: AbortSignal) {
  return api.get(`${base}/profiles/${id}`, { schema: billingProfileSchema, ...withSignal(signal) })
}

/** A five-minute link to the tax identification card. Fetched on click, never on load. */
export function fetchTaxIdCardLink(id: string) {
  return api.get(`${base}/profiles/${id}/tax-id-card`, {
    schema: z.object({ url: z.string(), expiresInSeconds: z.number() }),
  })
}

export function fetchInvoicePayments(id: string, signal?: AbortSignal) {
  return api.get(`${base}/invoices/${id}/payments`, {
    schema: invoicePaymentsSchema,
    ...withSignal(signal),
  })
}

/** Decimal strings on the way in — exact, and the server decides what the currency can hold. */
export type LineInput = {
  description: string
  quantity: string
  unitPrice: string
  discountAmount?: string
  taxRate?: string
}

export type InvoiceBody = {
  billToName: string
  billToEmail?: string
  billToAddress?: string
  billToTaxId?: string
  billToCompanyCategory?: CompanyCategory
  taxInvoiceCode?: TaxInvoiceCode
  currency: string
  paymentCurrency?: string
  paymentExchangeRate?: string
  dueAt?: string
  notes?: string
  internalNotes?: string
  lines: LineInput[]
}

export function createInvoice(body: InvoiceBody) {
  return api.post(`${base}/invoices`, { schema: invoiceSchema, body })
}

export function updateInvoice(id: string, body: Partial<InvoiceBody>) {
  return api.patch(`${base}/invoices/${id}`, { schema: invoiceSchema, body })
}

export function issueInvoice(id: string) {
  return api.post(`${base}/invoices/${id}/issue`, { schema: invoiceSchema })
}

const emailSentSchema = z.object({
  sentTo: z.string(),
  sentAt: z.string(),
  messageId: z.string().nullable(),
})

/**
 * The request sends the email. If the email provider refuses it, the API returns
 * `email_send_failed`.
 */
export function emailInvoice(id: string) {
  return api.post(`${base}/invoices/${id}/email`, { schema: emailSentSchema })
}

export function voidInvoice(id: string, reason: string) {
  return api.post(`${base}/invoices/${id}/void`, { schema: invoiceSchema, body: { reason } })
}

export function reissueInvoice(id: string) {
  return api.post(`${base}/invoices/${id}/reissue`, { schema: invoiceSchema })
}

export function setCompanyInvoiceStatus(id: string, companyInvoiceStatus: CompanyInvoiceStatus) {
  return api.patch(`${base}/invoices/${id}/company-invoice-status`, {
    schema: invoiceSchema,
    body: { companyInvoiceStatus },
  })
}

export function deleteInvoice(id: string) {
  return api.delete(`${base}/invoices/${id}`, { schema: z.undefined() })
}

/** What is still owed: the server's figures, clamped at zero. Subtraction of two server values. */
export function outstanding(invoice: Pick<Invoice, 'total' | 'amountPaid' | 'creditedTotal'>) {
  return Math.max(invoice.total - invoice.amountPaid - invoice.creditedTotal, 0)
}

/**
 * Open, and past its due date. Read from `dueAt` rather than `status`: `overdue` is only written
 * when something recomputes the invoice, so one nobody touched still says `issued`.
 */
export function isPastDue(invoice: Pick<Invoice, 'status' | 'dueAt'>, now = Date.now()): boolean {
  return (
    (invoice.status === 'issued' ||
      invoice.status === 'partially_paid' ||
      invoice.status === 'overdue') &&
    invoice.dueAt !== null &&
    Date.parse(invoice.dueAt) < now
  )
}

/**
 * An invoice's status, as a tone. `partially_paid` is `warning` rather than `success`: money has
 * arrived and the invoice is still outstanding. A green badge there is how a part-payment gets
 * filed as settled.
 */
const INVOICE_TONE: Record<InvoiceStatus, StatusTone> = {
  paid: 'success',
  partially_paid: 'warning',
  overdue: 'danger',
  voided: 'danger',
  issued: 'info',
  draft: 'neutral',
}

export function invoiceTone(status: InvoiceStatus): StatusTone {
  return INVOICE_TONE[status]
}

/**
 * The verbs each status allows, mirroring the server's refusals so the page does not offer what it
 * would refuse. The server checks every one of them again.
 */
export function invoiceActions(invoice: Pick<Invoice, 'status'>) {
  const open =
    invoice.status === 'issued' ||
    invoice.status === 'partially_paid' ||
    invoice.status === 'overdue'
  return {
    edit: invoice.status === 'draft',
    issue: invoice.status === 'draft',
    discard: invoice.status === 'draft',
    recordPayment: open,
    credit: open || invoice.status === 'paid',
    void: open,
    reissue: invoice.status === 'voided',
    // A draft has no number yet. A customer who gets a voided invoice can pay the wrong invoice.
    email: open || invoice.status === 'paid',
  }
}

/** `virtual_account` → `virtual account`. */
export function label(value: string): string {
  return value.replaceAll('_', ' ')
}
